import React, {useMemo} from 'react';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {ProductionCostScheduleWithProvidedValues} from '@/Pages/ProductionCost/store/types';
import LineErrors from './LineErrors/LineErrors';
import LineSettings from './LineSettings/LineSettings';
import style from './Lines.module.scss';
import {getProvidedValueErrorMessage} from './LineErrors/LineErrors.utils';

const Lines: React.FC = () => {
  const busValues = useAppSelector(
    ({productionCost}) => productionCost.busValues
  );
  const busScheduleValues = useAppSelector(
    ({productionCost}) => productionCost.busScheduleValues
  );
  const driverCostCalculationType = useAppSelector(
    ({productionCost}) => productionCost.driverCostCalculationType
  );
  const validationErrors = useAppSelector(
    ({productionCost}) => productionCost.validationErrors
  );
  const calculateProductionCostErrors = useAppSelector(
    ({productionCost}) => productionCost.serverErrors.calculateProductionCost
  );

  const lines: {
    [lineNumber: string]: ProductionCostScheduleWithProvidedValues[];
  } = useMemo(
    () =>
      (busScheduleValues ?? []).reduce((lineNumberToSchedules, schedule) => {
        if (!lineNumberToSchedules[schedule.lineNumber]) {
          lineNumberToSchedules[schedule.lineNumber] = [];
        }
        lineNumberToSchedules[schedule.lineNumber].push(schedule);
        return lineNumberToSchedules;
      }, {}),
    [busScheduleValues]
  );

  const lineNumberToServerErrors = useMemo(() => {
    if (!calculateProductionCostErrors?.invalidProvidedValues) {
      return null;
    }

    const {invalidProvidedValues} = calculateProductionCostErrors;
    const lineNumberToErrors = {};

    for (const err of invalidProvidedValues) {
      // Find line
      const values = busScheduleValues?.find(
        ({scheduleId}) => scheduleId === err.schedule.scheduleId
      );
      if (!values) {
        const stringifiedObj = JSON.stringify(err, null, 2);
        console.error(
          `Can't find bus schedule values for error: ${stringifiedObj}`
        );
        continue;
      }
      const {lineNumber, name} = values;

      // Find bus provided values entry
      const busProvidedValuesEntry = busValues?.find(
        ({vehicleId}) => vehicleId === err.bus.vehicleId
      );
      if (!busProvidedValuesEntry) {
        const stringifiedObj = JSON.stringify(err, null, 2);
        console.error(
          `Can't find bus provided values entry for error: ${stringifiedObj}`
        );
        continue;
      }

      const errorMessage = getProvidedValueErrorMessage(
        err.errorCode,
        name ?? '',
        busProvidedValuesEntry.vehicleName ?? ''
      );

      if (!lineNumberToErrors[lineNumber]) lineNumberToErrors[lineNumber] = [];
      lineNumberToErrors[lineNumber].push(errorMessage);
    }

    return lineNumberToErrors;
  }, [busValues, busScheduleValues, calculateProductionCostErrors]);

  if (!busValues) return null;

  return (
    <div className={style.container}>
      {Object.entries(lines).map(([lineNumber, schedules]) => (
        <div key={lineNumber} data-testid={`${lineNumber}-container`}>
          {lineNumberToServerErrors?.[lineNumber] && (
            <LineErrors errors={lineNumberToServerErrors[lineNumber]} />
          )}
          <LineSettings
            lineNumber={lineNumber}
            schedules={schedules}
            busValues={busValues}
            driverCostCalculationType={driverCostCalculationType}
            errors={validationErrors}
          />
        </div>
      ))}
    </div>
  );
};

export default React.memo(Lines);
